import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Search,
  X,
  LayoutGrid,
  MousePointerClick,
  Database,
  Wrench,
} from "lucide-react";

import { formatNumber } from "shared/utils/numberFormatter";

import axiosInstance from "shared/services/api/axiosInstance";
import { SpanKindIcon } from "shared/components/spans/KindTag";

/**
 * Module-level cache for entity lists (`agents` and `spans:<spanKind|all>`).
 * Survives component unmount/remount so re-opening the picker is instant
 * after the first fetch — same lifetime as the page session.
 */
const _entityListCache = new Map(); // key: 'agents' | `spans:${kind|'all'}` → rows[]
const _entityListInflight = new Map(); // key → Promise of rows[]

/** One label for lists: saved display name, else raw event name. */
function getEventListLabel(evt) {
  const dn = evt.displayName != null && String(evt.displayName).trim();
  if (dn) return String(evt.displayName).trim();
  return evt.name != null ? String(evt.name) : "";
}

function normalizeEventRow(evt) {
  const name = evt.name || evt.id;
  const count = evt.count != null ? parseInt(evt.count, 10) : undefined;
  const eventType = evt.eventType || evt.event_type || "custom";
  const displayName = evt.displayName || evt.display_name || null;
  const description = evt.description != null ? String(evt.description) : "";
  const tags = Array.isArray(evt.tags) ? evt.tags : [];
  const src = evt.source || (evt.type === "transaction" ? "transaction" : "event");
  return { name, count, eventType, displayName, description, tags, source: src, raw: evt };
}

/** Wraps the shared `Icon` so it has the same prop signature as a lucide
 *  icon — lets it stand in wherever the picker expects a NavIcon. */
function AgentNavIcon(props) {
  const { size = 14, className = "", style = {}, color = "currentColor" } = props;
  return <span className={`inline-flex ${className}`} style={style}><SpanKindIcon kind="agent" size={size} color={color} /></span>;
}

/** Short type tag shown on the right of each row (replaces per-row icons). */
function eventTypeTag(eventType) {
  switch (eventType) {
    case "agent":  return "agent";
    case "span":   return "span";
    case "custom": return "custom";
    case "auto":   return "auto";
    default:       return eventType || "event";
  }
}

// Entity tabs: pick events / agents / spans as a unified metric source.
//   agents → /insights/entities?kind=agent  (entityType emitted: 'agent')
//   spans  → /insights/entities?kind=span   (entityType emitted: 'span')
// For spans, an optional spanKind sub-chip filter narrows by kind
// (llm | tool | agent | retrieval | span) — also passed back to the parent.
const ENTITY_TABS = {
  agents: { kind: "agent", label: "Agents", Icon: AgentNavIcon },
  spans:  { kind: "span",  label: "Spans",  Icon: Wrench },
};
const SPAN_KINDS = [
  { id: "all",       label: "All",       value: null },
  { id: "tool",      label: "Tool",      value: "tool" },
  { id: "llm",       label: "LLM",       value: "llm" },
  { id: "agent",     label: "Agent",     value: "agent" },
  { id: "retrieval", label: "Retrieval", value: "retrieval" },
  // Canonical catch-all span kind is "span" ("generic" is only a legacy alias).
  { id: "span",      label: "Span",      value: "span" },
];

/**
 * Three-pane event picker — nav | list | description.
 * Design language matches the agent-runs PropertyPickerPopover: clean borders,
 * Inter, monospace labels, small uppercase type tags, no per-row icons.
 */
export default function EventsSelectorMenu({
  availableEvents = [],
  onSelect,
  onClose = null,
  maxHeight = 420,
  showSourceTabs = true,
}) {
  const [sidebarTab, setSidebarTab] = useState("all"); // 'all' | 'events' | 'agents' | 'spans'
  const [search, setSearch] = useState("");
  const [selectedName, setSelectedName] = useState(null);
  const [spanKindFilter, setSpanKindFilter] = useState(null); // null | 'llm' | 'tool' | 'agent' | 'retrieval' | 'generic'

  // Hydrate from module-level cache so re-opening the picker shows data
  // instantly. New fetches still update both the cache and local state.
  const [entityData, setEntityData] = useState(() => {
    const init = {};
    _entityListCache.forEach((rows, key) => { init[key] = rows; });
    return init;
  });
  const [entityLoading, setEntityLoading] = useState({});

  // Compute the active fetch key for the current tab + spanKind filter.
  const activeFetchKey = useMemo(() => {
    if (sidebarTab === "spans") return `spans:${spanKindFilter || "all"}`;
    if (sidebarTab === "agents") return "agents";
    return null;
  }, [sidebarTab, spanKindFilter]);

  useEffect(() => {
    if (!activeFetchKey) return;
    // Cache hit → state already has the data; nothing to do.
    if (_entityListCache.has(activeFetchKey)) {
      setEntityData((prev) => (prev[activeFetchKey] ? prev : { ...prev, [activeFetchKey]: _entityListCache.get(activeFetchKey) }));
      return;
    }

    // De-dup concurrent fetches across remounts.
    if (_entityListInflight.has(activeFetchKey)) {
      setEntityLoading((prev) => ({ ...prev, [activeFetchKey]: true }));
      _entityListInflight.get(activeFetchKey).then((rows) => {
        setEntityData((prev) => ({ ...prev, [activeFetchKey]: rows }));
        setEntityLoading((prev) => ({ ...prev, [activeFetchKey]: false }));
      });
      return;
    }

    const isSpan = sidebarTab === "spans";
    const params = isSpan
      ? { kind: "span", ...(spanKindFilter ? { spanKind: spanKindFilter } : {}) }
      : { kind: "agent" };

    setEntityLoading((prev) => ({ ...prev, [activeFetchKey]: true }));
    const promise = axiosInstance
      .get('/insights/entities', { params })
      .then((res) => {
        const rows = Array.isArray(res.data?.entities) ? res.data.entities : [];
        const withType = rows.map((r) => ({ ...r, eventType: isSpan ? "span" : "agent" }));
        const normalised = withType.map(normalizeEventRow);
        _entityListCache.set(activeFetchKey, normalised);
        return normalised;
      })
      .catch(() => {
        const empty = [];
        _entityListCache.set(activeFetchKey, empty);
        return empty;
      })
      .finally(() => {
        _entityListInflight.delete(activeFetchKey);
      });
    _entityListInflight.set(activeFetchKey, promise);
    promise.then((rows) => {
      setEntityData((prev) => ({ ...prev, [activeFetchKey]: rows }));
      setEntityLoading((prev) => ({ ...prev, [activeFetchKey]: false }));
    });
  }, [activeFetchKey, sidebarTab, spanKindFilter]);

  const eventsList = useMemo(() => {
    return (Array.isArray(availableEvents) ? availableEvents : [])
      .filter((evt) => {
        const src = evt.source || (evt.type === "transaction" ? "transaction" : "event");
        return src !== "transaction";
      })
      .map(normalizeEventRow);
  }, [availableEvents]);

  const filteredByTab = useMemo(() => {
    if (!showSourceTabs) return eventsList;
    if (sidebarTab === "agents" || sidebarTab === "spans") {
      return entityData[activeFetchKey] || [];
    }
    if (sidebarTab === "events") {
      return eventsList.filter((e) => e.eventType === "custom");
    }
    return eventsList;
  }, [eventsList, sidebarTab, showSourceTabs, entityData, activeFetchKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return filteredByTab;

    const hashIdx = q.indexOf("#");
    if (hashIdx >= 0) {
      const tagPart = q
        .slice(hashIdx)
        .split(/\s+/)[0]
        .replace(/^#/, "")
        .toLowerCase();
      const namePart = q.slice(0, hashIdx).trim().toLowerCase();
      return filteredByTab.filter((e) => {
        const tagMatch =
          !tagPart ||
          e.tags.some((t) => String(t).toLowerCase().includes(tagPart));
        const nameMatch =
          !namePart ||
          e.name.toLowerCase().includes(namePart) ||
          (e.displayName && e.displayName.toLowerCase().includes(namePart));
        return tagMatch && nameMatch;
      });
    }

    return filteredByTab.filter((e) => {
      const hay = `${e.name} ${e.displayName || ""} ${e.description}`.toLowerCase();
      return hay.includes(q);
    });
  }, [filteredByTab, search]);

  useEffect(() => {
    if (filtered.length === 0) {
      setSelectedName(null);
      return;
    }
    setSelectedName((prev) => {
      if (prev && filtered.some((e) => e.name === prev)) return prev;
      return filtered[0].name;
    });
  }, [filtered]);

  const selected = useMemo(
    () => filtered.find((e) => e.name === selectedName) || null,
    [filtered, selectedName]
  );

  const applyEvent = useCallback(
    (evtRow) => {
      if (!evtRow || !onSelect) return;
      // entityKind emitted to the parent: 'event' | 'agent' | 'span'
      let kind = "event";
      if (sidebarTab === "agents") kind = "agent";
      else if (sidebarTab === "spans") kind = "span";
      // Pass the spanKind so the parent can stash it on the metric/step.
      // Use the row's spanKind first (returned by /entities for spans) and
      // fall back to the active filter chip when the row doesn't carry it.
      const resolvedSpanKind = kind === "span"
        ? (evtRow.raw?.spanKind || evtRow.raw?.kind || spanKindFilter || null)
        : null;
      onSelect(evtRow.name, kind, { spanKind: resolvedSpanKind });
      if (onClose) onClose();
    },
    [onSelect, onClose, sidebarTab, spanKindFilter]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && onClose) onClose();
      if (e.key === "Enter" && selected && document.activeElement?.tagName !== "INPUT") {
        applyEvent(selected);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, selected, applyEvent]);

  const maxHeightPx = typeof maxHeight === "number" ? maxHeight : 420;

  const navTabs = useMemo(() => {
    const tabs = [
      { id: "all",    label: "All",    Icon: LayoutGrid },
      { id: "events", label: "Events", Icon: MousePointerClick },
    ];
    Object.entries(ENTITY_TABS).forEach(([tabKey, cfg]) => {
      tabs.push({ id: tabKey, label: cfg.label, Icon: cfg.Icon });
    });
    return tabs;
  }, []);

  return (
    <div
      className="rounded-xl border shadow-2xl overflow-hidden flex flex-col text-left bg-surface"
      style={{
        borderColor: "var(--border-default)",
        maxHeight: maxHeightPx,
        width: "100%",
        minHeight: 0,
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div className="flex flex-1 min-h-0">
        {/* ── Left nav ─────────────────────────────────────────────── */}
        {showSourceTabs && (
          <div
            className="flex w-[148px] flex-shrink-0 flex-col border-r"
            style={{ borderColor: "var(--border-default)", backgroundColor: "var(--surface-sunken)" }}
          >
            <div
              className="border-b px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-[color:var(--text-muted)]"
              style={{ borderColor: "var(--border-default)" }}
            >
              Categories
            </div>
            <div className="flex flex-1 flex-col overflow-y-auto py-1">
              {navTabs.map((t) => {
                const NavIcon = t.Icon;
                const active = sidebarTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSidebarTab(t.id)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors whitespace-nowrap"
                    style={{
                      backgroundColor: active ? "color-mix(in srgb, var(--brand-primary) 12%, transparent)" : "transparent",
                      color: active ? "var(--brand-primary)" : "var(--text-secondary)",
                      fontWeight: active ? 600 : 500,
                    }}
                  >
                    <NavIcon size={14} style={{ color: active ? "var(--brand-primary)" : "var(--text-disabled)" }} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Middle: search + list ───────────────────────────────── */}
        <div
          className="flex flex-1 min-w-0 flex-col border-r"
          style={{ borderColor: "var(--border-default)" }}
        >
          {/* Search */}
          <div
            className="flex items-center gap-2 border-b px-3 py-2 flex-shrink-0"
            style={{ borderColor: "var(--border-default)" }}
          >
            <Search size={14} className="text-[color:var(--text-muted)]" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search events, use # to filter by tag"
              className="flex-1 bg-transparent text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)] focus:outline-none"
            />
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="rounded p-1 text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
                title="Close"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Span sub-chips */}
          {sidebarTab === "spans" && (
            <div
              className="flex flex-wrap gap-1 px-3 py-2 border-b flex-shrink-0"
              style={{ borderColor: "var(--border-default)", backgroundColor: "var(--surface-sunken)" }}
            >
              {SPAN_KINDS.map((sk) => {
                const active = (spanKindFilter || null) === sk.value;
                return (
                  <button
                    key={sk.id}
                    type="button"
                    onClick={() => setSpanKindFilter(sk.value)}
                    className="px-2 py-0.5 rounded text-[11px] font-medium transition-colors"
                    style={{
                      backgroundColor: active ? "var(--brand-primary)" : "var(--surface-base)",
                      color: active ? "var(--ink-onBrand)" : "var(--text-secondary)",
                      border: `1px solid ${active ? "var(--brand-primary)" : "var(--border-default)"}`,
                    }}
                  >
                    {sk.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* List */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {entityLoading[activeFetchKey] ? (
              <div className="px-3 py-6 text-[12px] text-center text-[color:var(--text-muted)]">
                Loading…
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-6 text-[12px] text-center text-[color:var(--text-muted)]">
                {sidebarTab === "events"
                  ? "No custom events in this tab. Try All."
                  : sidebarTab === "agents"
                  ? "No agents tracked yet."
                  : sidebarTab === "spans"
                  ? `No ${spanKindFilter ? spanKindFilter + " " : ""}spans tracked yet.`
                  : "No matching events."}
              </div>
            ) : (
              filtered.map((evt) => {
                const isSel = evt.name === selectedName;
                const label = getEventListLabel(evt);
                return (
                  <button
                    key={evt.name}
                    type="button"
                    onClick={() => applyEvent(evt)}
                    onMouseEnter={() => setSelectedName(evt.name)}
                    className="flex w-full items-center justify-between px-3 py-1.5 text-left text-[12px] transition-colors"
                    style={{
                      backgroundColor: isSel ? "var(--surface-hover)" : "transparent",
                    }}
                  >
                    <span className="font-mono text-[color:var(--text-primary)] truncate">{label}</span>
                    <span className="ml-2 inline-flex items-center text-[10px] uppercase text-[color:var(--text-muted)] flex-shrink-0">
                      {eventTypeTag(evt.eventType)}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ── Right: detail pane ──────────────────────────────────── */}
        <div
          className="hidden sm:flex w-[260px] max-w-[38vw] flex-shrink-0 flex-col p-3 overflow-y-auto min-h-0"
          style={{ backgroundColor: "var(--surface-sunken)" }}
        >
          {selected ? (
            <>
              <div className="mb-3 min-w-0">
                <h3
                  className="text-[13px] font-semibold leading-tight truncate"
                  style={{ color: "var(--text-primary)" }}
                  title={getEventListLabel(selected)}
                >
                  {getEventListLabel(selected)}
                </h3>
                {selected.displayName != null && String(selected.displayName).trim() !== "" && (
                  <p
                    className="text-[11px] mt-0.5 truncate font-mono text-[color:var(--text-muted)]"
                    title={selected.name}
                  >
                    {selected.name}
                  </p>
                )}
              </div>

              <dl className="space-y-2 text-[11px]">
                <div>
                  <dt className="font-semibold uppercase tracking-wide text-[10px] text-[color:var(--text-muted)]">
                    Tracked as
                  </dt>
                  <dd className="mt-0.5 text-[color:var(--text-primary)]">
                    {selected.eventType === "agent"
                      ? "Agent run"
                      : selected.eventType === "span"
                      ? `Span${selected.raw?.spanKind ? ` · ${selected.raw.spanKind}` : ""}`
                      : selected.eventType === "auto"
                      ? "Auto-captured"
                      : "Custom"}
                  </dd>
                </div>
                <div>
                  <dt className="font-semibold uppercase tracking-wide text-[10px] text-[color:var(--text-muted)]">
                    Volume
                  </dt>
                  <dd className="mt-0.5 tabular-nums text-[color:var(--text-primary)]">
                    {selected.count != null && !Number.isNaN(selected.count)
                      ? `${formatNumber(selected.count)} events`
                      : "—"}
                  </dd>
                </div>
                {selected.tags.length > 0 && (
                  <div>
                    <dt className="font-semibold uppercase tracking-wide text-[10px] text-[color:var(--text-muted)]">
                      Tags
                    </dt>
                    <dd className="mt-0.5 flex flex-wrap gap-1">
                      {selected.tags.map((t) => (
                        <span
                          key={t}
                          className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                          style={{ backgroundColor: "color-mix(in srgb, var(--brand-primary) 10%, transparent)", color: "var(--brand-primary)" }}
                        >
                          #{t}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>

              <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border-default)" }}>
                <p className="text-[11px] leading-relaxed text-[color:var(--text-muted)]">
                  {selected.description || "No description available."}
                </p>
              </div>
            </>
          ) : (
            <p className="text-[11px] text-[color:var(--text-muted)]">
              Select an event to see details.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
